/**
 * Cookie Consent Banner
 *
 * Displays a consent banner for first-time visitors.
 * Cookies are essential for site functionality - declining redirects away.
 */

import React, { useState, useEffect } from "react";
import styles from "./CookieConsent.module.css";

const COOKIE_NAME = "stagecraft_cookie_consent";
const COOKIE_VERSION = "2026-01";
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

// Custom event dispatched when cookies are accepted
export const COOKIES_ACCEPTED_EVENT = "stagecraft:cookies-accepted";

interface ConsentData {
  consent: boolean;
  timestamp: string;
  version: string;
}

function getConsentCookie(): ConsentData | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === COOKIE_NAME) {
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch {
        return null;
      }
    }
  }
  return null;
}

/** Check if cookies have been accepted (for use by other components) */
export function areCookiesAccepted(): boolean {
  const consent = getConsentCookie();
  return consent?.consent === true && consent?.version === COOKIE_VERSION;
}

function setConsentCookie(consent: boolean): void {
  const data: ConsentData = {
    consent,
    timestamp: new Date().toISOString(),
    version: COOKIE_VERSION,
  };
  const value = encodeURIComponent(JSON.stringify(data));
  document.cookie = `${COOKIE_NAME}=${value}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const existing = getConsentCookie();
    // Show banner if no consent cookie or if version is outdated
    if (!existing || existing.version !== COOKIE_VERSION) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    setConsentCookie(true);
    setIsVisible(false);
    // Notify other components that cookies have been accepted
    window.dispatchEvent(new CustomEvent(COOKIES_ACCEPTED_EVENT));
  };

  const handleDecline = () => {
    // Redirect to DuckDuckGo
    window.location.href = "https://duckduckgo.com";
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.banner}>
      <div className={styles.content}>
        <div className={styles.text}>
          <h3 className={styles.title}>Cookies Required</h3>
          <p className={styles.description}>
            Stagecraft uses cookies that are essential for the website and
            application to function. These cookies enable core features like
            authentication, saving your work, and remembering your preferences.
            Without them, the site cannot operate.
          </p>
        </div>
        <div className={styles.actions}>
          <button onClick={handleAccept} className={styles.acceptButton}>
            Accept
          </button>
          <button onClick={handleDecline} className={styles.declineButton}>
            No Thanks
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
